import React, { useState } from "react";

import { Button } from "~/components/ui/button";
import { Textarea } from "~/components/ui/textarea";
import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import TabSectionHeader from "~/pages/clients/components/client-detail/TabSectionHeader";
import { Client } from "~/types/client";

interface InternalNotesTabProps {
  client: Client;
  onClientUpdated?: () => void;
  /** When true the notes are shown read-only (e.g. for sellers). */
  readOnly?: boolean;
}

const InternalNotesTab: React.FC<InternalNotesTabProps> = ({
  client,
  onClientUpdated,
  readOnly = false,
}) => {
  const [notes, setNotes] = useState(client.internal_notes || "");
  const [isUpdating, setIsUpdating] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const handleNotesChange = (value: string) => {
    setNotes(value);
    setHasChanges(value !== (client.internal_notes || ""));
  };

  const handleSave = async () => {
    setIsUpdating(true);
    try {
      const { error } = await supabase
        .from("clients")
        .update({ internal_notes: notes })
        .eq("id", client.id);

      if (error) throw error;

      toast({
        title: "Notas actualizadas",
        description: "Las notas internas han sido guardadas exitosamente.",
      });

      setHasChanges(false);

      // Notify parent component about the update
      if (onClientUpdated) {
        onClientUpdated();
      }

      // Dispatch a custom event to notify about client update
      window.dispatchEvent(
        new CustomEvent("client-updated", {
          detail: { clientId: client.id },
        })
      );
    } catch (error) {
      console.error("Error updating internal notes:", error);
      toast({
        title: "Error",
        description: "No se pudieron actualizar las notas internas.",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancel = () => {
    setNotes(client.internal_notes || "");
    setHasChanges(false);
  };

  return (
    <div className="space-y-6 p-4 pt-6 md:p-6">
      <TabSectionHeader
        title="Notas Internas"
        description="Solo para uso interno — no se comparte con el cliente."
      />

      {readOnly ? (
        <div className="min-h-[200px] whitespace-pre-wrap rounded-md border border-input bg-muted/40 p-3 text-sm text-gray-700">
          {client.internal_notes?.trim() ? (
            client.internal_notes
          ) : (
            <span className="text-muted-foreground">
              No hay notas internas registradas.
            </span>
          )}
        </div>
      ) : (
        <>
          <Textarea
            placeholder="Escriba notas internas sobre el cliente..."
            value={notes}
            onChange={(e) => handleNotesChange(e.target.value)}
            className="min-h-[200px]"
            rows={8}
          />

          {hasChanges && (
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isUpdating}
              >
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={isUpdating}>
                {isUpdating ? "Guardando..." : "Guardar Cambios"}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InternalNotesTab;
